import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { getStatLabel, getStatValueKind } from '../../constants/dashboardStats.catalog';
import { isTrackableKpi, kpiAlertFires, kpiTargetValue } from '../../constants/kpiTargets';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { KPILimit } from '../../stores/kpi.store';
import { contentBorders } from '../../theme/createContentTheme';
import type { OrganizationType } from '../../types/organization.type';
import StatusChip from './StatusChip';

const SYMBOL: Record<KPILimit['operator'], string> = { '>': '>', '>=': '≥', '<': '<', '<=': '≤', '=': '=' };

/** Alerts first, then limits within range, then rules without a reading. */
const rank = (alerting: boolean | null) => (alerting === true ? 0 : alerting === false ? 1 : 2);

interface KpiTargetsTableProps {
  limits: KPILimit[];
  /** The statistics object of the period shown. */
  source: object | null;
  orgType?: OrganizationType | null;
}

/**
 * Enabled KPI limits against the period's value: one row per rule (a KPI can
 * have several), matched on the metric key. A limit is an alert condition
 * ("alert when completion rate < 70%"), so a row is "Alert" when the condition
 * holds and "Within limit" otherwise. A missing reading says "No data" rather
 * than counting as 0. Alerts sort first.
 */
const KpiTargetsTable: React.FC<KpiTargetsTableProps> = ({ limits, source, orgType }) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const rows = limits
    .filter((l) => l.isEnabled && isTrackableKpi(l.name))
    .map((limit) => {
      const value = kpiTargetValue(source, limit.name);
      return { limit, value, alerting: kpiAlertFires(value, limit.operator, limit.value) };
    })
    .sort((a, b) => rank(a.alerting) - rank(b.alerting));

  if (rows.length === 0) return null;

  return (
    <TableContainer
      sx={(theme) => ({
        bgcolor: 'background.paper',
        border: `1px solid ${contentBorders(theme).default}`,
        borderRadius: `${CONTENT_SHAPE.md}px`,
      })}
    >
      <Table size="small" aria-label={t('dashboard.targets.title')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('dashboard.targets.metric')}</TableCell>
            <TableCell align="right">{t('dashboard.targets.alertWhen')}</TableCell>
            <TableCell align="right">{t('dashboard.targets.actual')}</TableCell>
            <TableCell sx={{ width: { md: '28%' } }}>{t('dashboard.targets.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ limit, value, alerting }) => {
            const kind = getStatValueKind(limit.name.toLowerCase());
            return (
              <TableRow key={limit.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {getStatLabel(t, limit.name, orgType)}
                  </Typography>
                  {limit.alertMessage && alerting === true ? (
                    <Typography variant="caption" color="text.secondary" component="p">
                      {limit.alertMessage}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Box component="bdi" dir="ltr">
                    {SYMBOL[limit.operator]} {fmt.withUnit(kind, limit.value)}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  <bdi>{value === null ? '—' : fmt.withUnit(kind, value)}</bdi>
                </TableCell>
                <TableCell>
                  <StatusChip
                    tone={alerting === null ? 'offline' : alerting ? 'attention' : 'complete'}
                    label={
                      alerting === null
                        ? t('dashboard.targets.noData')
                        : alerting
                          ? t('dashboard.targets.alert')
                          : t('dashboard.targets.withinLimit')
                    }
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default KpiTargetsTable;
